/**
 * FunShine Procedural Arcade Audio Synthesizer & Haptic Engine
 * Generates crisp 8-bit / 16-bit sound effects using the Web Audio API without any external asset loading.
 */

class SoundEngine {
  constructor() {
    this.ctx = null;
    this.enabled = true;
    this.volume = 0.5;
    this.hapticsEnabled = true;

    // Load saved preferences
    try {
      const savedAudio = localStorage.getItem('funshine_sfx_enabled');
      if (savedAudio !== null) this.enabled = savedAudio === 'true';

      const savedVol = localStorage.getItem('funshine_sfx_volume');
      if (savedVol !== null) this.volume = parseFloat(savedVol);

      const savedHaptics = localStorage.getItem('funshine_haptics_enabled');
      if (savedHaptics !== null) this.hapticsEnabled = savedHaptics === 'true';
    } catch (e) {
      // Ignore storage restrictions
    }
  }

  init() {
    if (!this.ctx && typeof window !== 'undefined') {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        this.ctx = new AudioCtx();
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  setVolume(vol) {
    this.volume = Math.max(0, Math.min(1, vol));
    try {
      localStorage.setItem('funshine_sfx_volume', String(this.volume));
    } catch (e) {}
  }

  setEnabled(val) {
    this.enabled = !!val;
    try {
      localStorage.setItem('funshine_sfx_enabled', String(this.enabled));
    } catch (e) {}
  }

  setHapticsEnabled(val) {
    this.hapticsEnabled = !!val;
    try {
      localStorage.setItem('funshine_haptics_enabled', String(this.hapticsEnabled));
    } catch (e) {}
  }

  vibrate(pattern = [20]) {
    if (!this.hapticsEnabled || typeof navigator === 'undefined' || !navigator.vibrate) return;
    try {
      navigator.vibrate(pattern);
    } catch (e) {}
  }

  /**
   * Play a procedural frequency sweep tone
   */
  playTone(freqStart, freqEnd, duration, type = 'sine', gainVal = 0.2) {
    if (!this.enabled) return;
    this.init();
    if (!this.ctx) return;

    try {
      const now = this.ctx.currentTime;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      osc.type = type;
      osc.frequency.setValueAtTime(freqStart, now);
      if (freqEnd && freqEnd !== freqStart) {
        osc.frequency.exponentialRampToValueAtTime(Math.max(10, freqEnd), now + duration);
      }

      const peakGain = gainVal * this.volume;
      gain.gain.setValueAtTime(peakGain, now);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

      osc.connect(gain);
      gain.connect(this.ctx.destination);

      osc.start(now);
      osc.stop(now + duration);
    } catch (e) {
      // Ignore audio synthesis errors on older devices
    }
  }

  /**
   * Play standard arcade sound effects
   */
  playSfx(name) {
    if (!this.enabled) return;

    switch (name) {
      case 'tap':
        this.playTone(480, 240, 0.05, 'triangle', 0.15);
        this.vibrate([10]);
        break;

      case 'place':
        this.playTone(320, 160, 0.08, 'sine', 0.25);
        this.vibrate([18]);
        break;

      case 'hit':
        // Explosive burst tone
        this.playTone(180, 40, 0.22, 'sawtooth', 0.4);
        this.vibrate([40, 30, 40]);
        break;

      case 'miss':
        // Splash/water droplet pitch drop
        this.playTone(600, 300, 0.15, 'sine', 0.18);
        this.vibrate([15]);
        break;

      case 'sunk':
        // Sinking fanfare
        this.playTone(260, 60, 0.4, 'sawtooth', 0.45);
        setTimeout(() => this.playTone(180, 40, 0.4, 'triangle', 0.3), 100);
        this.vibrate([60, 40, 80]);
        break;

      case 'success':
        // Happy ascending 2-tone chime
        this.playTone(523.25, 659.25, 0.12, 'triangle', 0.25); // C5 -> E5
        setTimeout(() => this.playTone(659.25, 783.99, 0.18, 'triangle', 0.25), 100); // G5
        this.vibrate([20, 30, 20]);
        break;

      case 'victory':
        // 4-tone victory fanfare
        this.playTone(523.25, 523.25, 0.12, 'triangle', 0.3); // C5
        setTimeout(() => this.playTone(659.25, 659.25, 0.12, 'triangle', 0.3), 120); // E5
        setTimeout(() => this.playTone(783.99, 783.99, 0.14, 'triangle', 0.3), 240); // G5
        setTimeout(() => this.playTone(1046.50, 1046.50, 0.35, 'triangle', 0.35), 360); // C6
        this.vibrate([50, 50, 50, 50, 100]);
        break;

      case 'defeat':
        // Descending sad tones
        this.playTone(400, 350, 0.2, 'sawtooth', 0.25);
        setTimeout(() => this.playTone(350, 280, 0.25, 'sawtooth', 0.25), 180);
        setTimeout(() => this.playTone(280, 160, 0.4, 'sawtooth', 0.25), 380);
        this.vibrate([80, 40, 120]);
        break;

      case 'bull':
        // Crisp high bell for Bull
        this.playTone(880, 880, 0.15, 'sine', 0.3);
        this.vibrate([25]);
        break;

      case 'cow':
        // Warm low bell for Cow
        this.playTone(440, 440, 0.15, 'sine', 0.2);
        this.vibrate([15]);
        break;

      case 'error':
        // Low buzzy error sound
        this.playTone(140, 120, 0.18, 'sawtooth', 0.25);
        this.vibrate([40, 40, 40]);
        break;

      case 'turn':
        // Subtle alert when it's your turn
        this.playTone(700, 880, 0.12, 'sine', 0.2);
        this.vibrate([20, 20]);
        break;

      default:
        this.playTone(440, 440, 0.08, 'sine', 0.1);
    }
  }
}

export const sound = new SoundEngine();
